import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { FaTimes } from "react-icons/fa";

const languageOptions = [
  { value: "en", label: "English" },
  { value: "pl", label: "Polish" },
  { value: "cs", label: "Czech" },
  { value: "ro", label: "Romanian" },
  { value: "hu", label: "Hungarian" },
];

interface ITranslateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onShowOriginal: () => void;
  onSelectTranslatedLang: (lang: string) => void;
}

export const TranslateModal: React.FC<ITranslateModalProps> = ({
  isOpen,
  onClose,
  onShowOriginal,
  onSelectTranslatedLang,
}) => {
  const { t } = useTranslation();
  const savedLanguage = localStorage.getItem("language");
  const [selectedLang, setSelectedLang] = React.useState(
    () =>
      languageOptions.find((option) => option.value === savedLanguage) ||
      languageOptions[0],
  );
  const modalRef = useRef<HTMLDivElement>(null);
  const darkMode = localStorage.getItem("darkMode") === "true";

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    }
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      setSelectedLang(
        languageOptions.find((option) => option.value === savedLanguage) ||
          languageOptions[0],
      );
    }
  }, [isOpen, savedLanguage]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div
        ref={modalRef}
        className="modal-panel p-4 w-1/2 max-w-xl min-w-96"
      >
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-xl font-semibold title-base-color">
            {t("translateModal.translatePageFrom")}
          </h2>
          <button
            onClick={onClose}
            className="modal-close-btn"
            tabIndex={-1}
          >
            <FaTimes className="text-xl text-gray-500 hover:text-del-red" />
          </button>
        </div>
        <div>
          <label className="block font-semibold mb-1 title-base-color">
            {t("translateModal.translateTo")}
          </label>
          <Select
            isSearchable={false}
            options={languageOptions}
            value={selectedLang}
            onChange={(option: any) => setSelectedLang(option)}
            classNamePrefix="react-select"
            styles={{
              control: (base) => ({
                ...base,
                backgroundColor: darkMode ? "#4B5563" : "#E5E7EB",
                color: darkMode ? "#D1D5DB" : "#374151",
                borderColor: darkMode ? "#374151" : "#9CA3AF",
                width: "full",
                outlineColor: "#86BC25",
                fontSize: "14px",
              }),
              singleValue: (base) => ({
                ...base,
                color: darkMode ? "#D1D5DB" : "#374151",
              }),
              menu: (base) => ({
                ...base,
                backgroundColor: darkMode ? "#374151" : "#FFFFFF",
                marginTop: 0,
              }),
              option: (base, { isFocused }) => ({
                ...base,
                backgroundColor: isFocused
                  ? "#86BC25"
                  : darkMode
                    ? "#374151"
                    : "#FFFFFF",
                color: darkMode ? "#FFFFFF" : "#000000",
              }),
              dropdownIndicator: (base) => ({
                ...base,
                paddingRight: "2px",
                paddingLeft: "2px",
              }),
            }}
          />
        </div>
        <div className="flex gap-2 mt-3 text-sm">
          <button
            className="bg-gray-300 hover:bg-gray-400 py-1 px-3 rounded w-1/2"
            onClick={() => {
              onShowOriginal();
              onClose();
            }}
          >
            {t("translateModal.showOriginal") || "Cancel"}
          </button>
          <button
            className="btn-green text-white py-1 px-3 rounded w-1/2"
            onClick={() => {
              onSelectTranslatedLang(selectedLang.label);
              onClose();
            }}
          >
            {t("translateModal.translate")}
          </button>
        </div>
      </div>
    </div>
  );
};
